import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Checkbox, Divider, Flex, Typography } from 'antd';
import { CustomButton, CustomTitle } from '@/components';
import ShipmentAlertThresholdInput from '@/pages/Shipments/PerShipmentNotification/components/ShipmentAlertThresholdInput';
import type {
  AlertThreshold,
  SelectedThresholdValues,
} from '@/pages/Shipments/PerShipmentNotification/components/AlertFieldByType';

import './RolesAndPermissions.css';

const { Text } = Typography;

interface Shipment {
  id?: string;
  sensor?: unknown;
}

interface ShipmentAlertThresholdsProps {
  shipment: Shipment;
  alertThreshold: AlertThreshold[];
  selectedThresholdValues: SelectedThresholdValues;
  setSelectedThresholdValues: (
    value:
      | SelectedThresholdValues
      | ((prev: SelectedThresholdValues) => SelectedThresholdValues)
  ) => void;
  selectAllAlertTypes: boolean;
  setSelectAllAlertTypes: (value: boolean) => void;
  maxHeight?: string;
  enableAllThresholds?: boolean;
  selectAllByDefault?: boolean;
  onAlertsReset: () => void;
  onAlertsApply: () => void;
}

const ShipmentAlertThresholds: React.FC<ShipmentAlertThresholdsProps> = ({
  shipment,
  alertThreshold,
  onAlertsReset,
  onAlertsApply,
  selectedThresholdValues,
  setSelectedThresholdValues,
  selectAllAlertTypes,
  setSelectAllAlertTypes,
  maxHeight = '500px',
  enableAllThresholds = false,
}) => {
  const didAutoSelectAll = useRef(false);

  const isRuleDisabled = useCallback(
    (rule: AlertThreshold['alerts'][number]) =>
      rule.sensor_required === 'required' &&
      !shipment?.sensor &&
      !enableAllThresholds,
    [enableAllThresholds, shipment?.sensor]
  );

  const allSelectableAlertTypes = useMemo(() => {
    const result: string[] = [];
    alertThreshold.forEach((group) => {
      group.alerts.forEach((rule) => {
        if (!isRuleDisabled(rule)) result.push(rule.alert_type);
      });
    });
    return result;
  }, [alertThreshold, isRuleDisabled]);

  const selectedCount = Object.keys(selectedThresholdValues).length;
  const totalCount = allSelectableAlertTypes.length;

  // useEffect(() => {
  //   debugger
  //   if (!selectAllByDefault) return;
  //   if (didAutoSelectAll.current) return;
  //   if (selectedCount > 0) return;
  //   if (totalCount === 0) return;

  //   const values: SelectedThresholdValues = {};
  //   alertThreshold.forEach((group) => {
  //     group.alerts.forEach((rule) => {
  //       if (!isRuleDisabled(rule))
  //         values[rule.alert_type] = rule.threshold_value;
  //     });
  //   });

  //   didAutoSelectAll.current = true;
  //   setSelectAllAlertTypes(true);
  //   setSelectedThresholdValues(values);
  // }, [
  //   alertThreshold,
  //   isRuleDisabled,
  //   selectAllByDefault,
  //   selectedCount,
  //   setSelectAllAlertTypes,
  //   setSelectedThresholdValues,
  //   totalCount,
  // ]);

  useEffect(() => {
    const isAll = totalCount > 0 && selectedCount === totalCount;
    if (selectAllAlertTypes !== isAll) setSelectAllAlertTypes(isAll);
  }, [selectedCount, selectAllAlertTypes, setSelectAllAlertTypes, totalCount]);

  const onSelectAllChange = (checked: boolean) => {
    didAutoSelectAll.current = true;

    if (!checked) {
      setSelectAllAlertTypes(false);
      setSelectedThresholdValues({});
      return;
    }

    const values: SelectedThresholdValues = {};
    alertThreshold.forEach((group) => {
      group.alerts.forEach((rule) => {
        if (!isRuleDisabled(rule))
          values[rule.alert_type] = rule.threshold_value;
      });
    });

    setSelectAllAlertTypes(true);
    setSelectedThresholdValues(values);
  };

  const togglePhase = (group: AlertThreshold, checked: boolean) => {
    didAutoSelectAll.current = true;
    setSelectAllAlertTypes(false);

    setSelectedThresholdValues((prev) => {
      const next: SelectedThresholdValues = { ...prev };
      group.alerts.forEach((rule) => {
        if (isRuleDisabled(rule)) return;
        if (checked) next[rule.alert_type] = rule.threshold_value;
        else delete next[rule.alert_type];
      });
      return next;
    });
  };

  const getPhaseState = (group: AlertThreshold) => {
    const selectable = group.alerts.filter((r) => !isRuleDisabled(r));
    const keys = selectable.map((r) => r.alert_type);
    const selected = keys.filter((k) => k in selectedThresholdValues).length;

    return {
      checked: keys.length > 0 && selected === keys.length,
      indeterminate: selected > 0 && selected < keys.length,
      disabled: keys.length === 0,
    };
  };

  return (
    <div className='shipmentAlerts' style={{ maxHeight }}>
      {/* Sticky header */}
      <div className='shipmentAlerts__header'>
        <CustomTitle
          text='Viewable Alert types'
          fontWeight={600}
          fontSize={12}
          lineHeight='16px'
          color='var(--text-secondary)'
          marginBottom={0}
        />
      </div>

      <div className='shipmentAlerts__content'>
        <div className='shipmentAlerts__contentInner'>
          <Flex align='center' className='shipmentAlerts__row'>
            <Checkbox
              className='custom-dropdown-export-coc-checkbox'
              checked={selectAllAlertTypes}
              onChange={(e) => onSelectAllChange(e.target.checked)}
            />
            <Text className='shipmentAlerts__selectAllText'>Select all</Text>
          </Flex>

          <Divider className='shipmentAlerts__divider' />

          {alertThreshold.map((group, index) => {
            const phase = getPhaseState(group);

            return (
              <React.Fragment key={group.phase_display}>
                {index !== 0 && (
                  <Divider className='shipmentAlerts__dividerTight' />
                )}

                <Flex align='center' className='shipmentAlerts__row'>
                  <Checkbox
                    className='custom-dropdown-export-coc-checkbox'
                    checked={phase.checked}
                    disabled={phase.disabled}
                    onChange={(e) => togglePhase(group, e.target.checked)}
                  />
                  <CustomTitle
                    text={group.phase_display}
                    fontSize={14}
                    fontWeight='600'
                    marginTop={0}
                    marginBottom={0}
                  />
                </Flex>

                <Flex vertical className='shipmentAlerts__rules'>
                  {group.alerts.map((rule) => {
                    const isSelected =
                      rule.alert_type in selectedThresholdValues;
                    const disabled = isRuleDisabled(rule);

                    return (
                      <div
                        key={rule.alert_type}
                        className={`shipmentAlerts__rule ${
                          isSelected ? 'shipmentAlerts__rule--active' : ''
                        } ${disabled ? 'shipmentAlerts__rule--disabled' : ''}`}
                      >
                        <ShipmentAlertThresholdInput
                          applyCheckboxClass
                          checkboxClassName='custom-dropdown-export-coc-checkbox'
                          gap={16}
                          shipment={shipment}
                          rule={rule}
                          isChecked={isSelected}
                          enableAllThresholds={enableAllThresholds}
                          onCheckboxChange={(checked) => {
                            didAutoSelectAll.current = true;
                            setSelectAllAlertTypes(false);
                            setSelectedThresholdValues((prev) => {
                              const next = { ...prev };
                              if (checked)
                                next[rule.alert_type] = rule.threshold_value;
                              else delete next[rule.alert_type];
                              return next;
                            });
                          }}
                        />
                      </div>
                    );
                  })}
                </Flex>
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className='shipmentAlerts__actions'>
        <CustomButton
          text='Reset'
          bgColor='transparent'
          color='var(--text-primary)'
          fontSize='12px'
          fontWeight='600'
          boxShadow='none'
          height='28px'
          width='auto'
          border='none'
          borderRadius='8px'
          onClick={onAlertsReset}
        />

        <CustomButton
          text='Apply'
          bgColor='#000'
          color='#fff'
          fontSize='12px'
          fontWeight='600'
          boxShadow='none'
          height='28px'
          width='auto'
          borderRadius='8px'
          onClick={onAlertsApply}
        />
      </div>
    </div>
  );
};

export default ShipmentAlertThresholds;
